import { useEventListener, watchDebounced } from '@vueuse/core';
import { cloneDeep } from 'lodash';

export const useUndo = <T extends any>(
    data: Ref<T>,
    opts?: {
        /**
         * Should the history be committed automatically on every change?
         * @default true
         */
        autoCommit?: MaybeRefOrGetter<boolean>;
        /**
         * Debounce time for committing the history.
         * @default 50
         */
        commitDebounce?: MaybeRefOrGetter<number>;
    },
) => {
    const history = ref<T[]>([cloneDeep(toValue(data))]);
    const historyIndex = ref<number>(0);
    const updateNumber = ref(0);
    const ignoreUpdateNumber = ref(0);

    const shouldIgnoreUpdate = computed(() => ignoreUpdateNumber.value >= updateNumber.value);

    const current = computed<T>({
        get: () => (historyIndex.value < 0 ? undefined : (history.value[historyIndex.value] as any)),
        set: value => {
            if (shouldIgnoreUpdate.value) {
                return;
            }
            data.value = value;
        },
    });

    watchDebounced(
        () => toValue(data),
        newData => {
            if (!shouldIgnoreUpdate.value && toValue(opts?.autoCommit) !== false) {
                // console.log(
                //     'auto commit to ',
                //     historyIndex.value + 1,
                //     ' and value is ',
                //     newData,
                //     ' and update number is ',
                //     updateNumber.value
                // );
                history.value = history.value.slice(0, historyIndex.value + 1);
                history.value.push(cloneDeep(newData as any));
                historyIndex.value = history.value.length - 1;
            }
            updateNumber.value++;
        },
        { deep: true, debounce: () => toValue(opts?.commitDebounce) ?? 200 },
    );

    const undo = () => {
        if (historyIndex.value > 0) {
            // console.log('undo to ', historyIndex.value - 1, ' and value is ', history.value[historyIndex.value - 1]);
            ignoreUpdateNumber.value = updateNumber.value;
            historyIndex.value--;
            data.value = history.value[historyIndex.value] as any;
        }
    };

    const redo = () => {
        if (historyIndex.value < history.value.length - 1) {
            // console.log('redo to ', historyIndex.value + 1, ' and value is ', history.value[historyIndex.value]);
            ignoreUpdateNumber.value = updateNumber.value;
            historyIndex.value++;
            data.value = history.value[historyIndex.value] as any;
        }
    };

    useEventListener('keydown', event => {
        // Undo
        if (event.ctrlKey && event.key.toLowerCase() === 'z' && !event.shiftKey) {
            event.preventDefault();
            undo();
        }
        // Redo
        if (
            (event.ctrlKey && event.key.toLowerCase() === 'y') ||
            (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'z')
        ) {
            event.preventDefault();
            redo();
        }
    });

    return {
        current,
        history,
        undo,
        redo,
    };
};
